import React, { PropTypes } from 'react'

import { load, schemas } from 'api'
import { applyHOCs, selectSlug } from 'helpers'
import { page, pageLoadSelectors, NotFoundPage } from 'page'

import { PageBlockRenderer } from 'core/components/PageBlockRenderer'
import resolveBranding from './resolveBranding'
import InstitutionHero from './sections/InstitutionHero'
import ActivateMembershipCard from './sections/ActivateMembershipCard'
import { UpcomingWebinars, UpcomingPrograms } from './sections/UpcomingEvents'
import SupportSection from './sections/SupportSection'

import css from './sections/style.scss'

export const InstitutionLandingPage = ({ institution = {}, events = [] }) => {
  const config = institution.landing_page_config || {}

  if (!config.enabled) {
    return <NotFoundPage />
  }

  const branding = resolveBranding(config)
  const textColor = branding.headingColor
  const linkColor = branding.linkColor
  const logoUrl = institution.logo_image_url || null
  const displayName = config.abbreviated_name || institution.name || ''

  const heroHeadline = config.hero_headline ||
    `NCFDD and ${displayName} are excited to partner together and provide all faculty, staff, administrators and graduate students with resources to help support them in their role.`
  const heroSubtitle = config.hero_subtitle ||
    `Welcome to your NCFDD member benefits. Through ${displayName}’s partnership with NCFDD, you have access to a wide range of professional development resources designed to support your success as a faculty member, scholar, researcher, educator, or academic leader. Explore webinars, programs, courses, tools, and community opportunities focused on writing and research productivity, career advancement, mentoring, leadership development, wellbeing, and more.`

  return (
    <div className={css.landingPage}>
      <InstitutionHero
        logoUrl={logoUrl}
        institutionName={institution.name || ''}
        branding={branding}
        showInstitutionName={config.show_institution_name}
        poweredByText={config.powered_by_text}
      />

      <div className={css.heroBanner}>
        <div className={css.heroBannerLeft}>
          <h1 className={css.heroBannerHeadline} style={textColor ? { color: textColor } : {}}>
            {heroHeadline}
          </h1>
          <p className={css.heroBannerSubtitle}>
            {heroSubtitle}
          </p>
        </div>
        <ActivateMembershipCard
          institutionName={institution.name}
          institutionSlug={institution.slug}
          config={config}
          buttonBackground={branding.buttonBackground}
          textColor={textColor}
        />
      </div>

      <div className={css.contentArea}>
        <div className={css.contentLeft}>
          <PageBlockRenderer blocks={institution.page_blocks || []} blockProps={{ textColor, linkColor }} />
        </div>
        <div className={css.contentRight}>
          <UpcomingWebinars events={events} textColor={textColor} linkColor={linkColor} />
          <UpcomingPrograms events={events} textColor={textColor} linkColor={linkColor} subtitle={config.upcoming_programs_subtitle} />
        </div>
      </div>

      <SupportSection config={config} textColor={textColor} />
    </div>
  )
}

InstitutionLandingPage.propTypes = {
  institution: PropTypes.shape({
    name: PropTypes.string,
    hero_image_url: PropTypes.string,
    landing_page_config: PropTypes.object
  }),
  events: PropTypes.arrayOf(PropTypes.object)
}

export default applyHOCs([
  page({ ...pageLoadSelectors(['institution']), layout: 'bare' }),
  load({
    institution: {
      urlSelector: (state, props) => `/v2/institutions/slug/${props.slug}`,
      schema: schemas.institution
    },
    events: {
      urlSelector: (state, props) => `/v2/events?filters[public_preview]=true&filters[institution_slug]=${encodeURIComponent(props.slug)}`,
      schema: [schemas.event]
    }
  }),
  selectSlug({ paramName: 'institutionSlug' })
])(InstitutionLandingPage)
